// 兵糧（スタミナ）。4分で1回復、最大 180+主Lv÷9（+兵糧庫）。最大のときの自然回復は予備へ（最大900+兵糧庫、課金の予備枠で+720）。
// 主Lvアップでは回復しない（2026-10-17 廃止）。未クリアのステージは勝てば全額返却。
// 月パスの期間中は自然回復が monthly.regenSpeed 倍速。1日（JST）に自然回復する量は無料と同じ monthly.dailyRegen まで（届いたら翌0時から）。
import { CONFIG } from './config';
import { levelTier } from './level';
import { chapterOf, type Profile, type StaminaState } from './profile';

const period = () => CONFIG.stamina.regenMinutes * 60_000;

export function staminaMax(profile: Profile): number {
  // 旧Lv1つ（主Lv9つ）ごとに+1
  return CONFIG.stamina.baseMax + levelTier(profile.level) + (profile.village?.levels.hyorogura ?? 0) * CONFIG.village.hyorograMaxPerLevel;
}
export function staminaReserveMax(profile: Profile): number {
  const paid = (profile.entitlements ?? []).includes('reserve_plus') ? CONFIG.stamina.paidReserveBonus : 0;
  return CONFIG.stamina.reserveMax + (profile.village?.levels.hyorogura ?? 0) * CONFIG.village.hyorograReservePerLevel + paid;
}

export interface StaminaView {
  value: number;
  reserve: number;
  max: number;
  reserveMax: number;
  /** 次に1増える時刻（満タンなら null） */
  nextAt: number | null;
  state: StaminaState;
}

const JST = 9 * 3_600_000;
const DAY = 86_400_000;
const jstDayOf = (t: number) => new Date(t + JST).toISOString().slice(0, 10);
/** t より後の JST 0時 */
const nextJstMidnight = (t: number) => Math.floor((t + JST) / DAY) * DAY + DAY - JST;

/** 現在の兵糧（state は書き換えない。確定した状態は view.state） */
export function staminaNow(state: StaminaState, now: number, profile: Profile): StaminaView {
  const max = staminaMax(profile);
  const rmax = staminaReserveMax(profile);
  let { value, reserve, at } = state;
  let day = state.day;
  let got = state.got ?? 0;
  const p = period();
  if (now < at) at = now;
  // 月パスの期間中：1回復ずつ進める（日ごとの上限を数えるため。3日空けても数千回）
  const until = profile.monthlyUntil ?? 0;
  const fp = p / CONFIG.monthly.regenSpeed;
  const cap = CONFIG.monthly.dailyRegen;
  let fastNext: number | null = null;
  if (until > at) {
    const end = Math.min(now, until);
    for (;;) {
      if (value >= max && reserve >= rmax) {
        at = end;
        break;
      }
      const t = at + fp;
      if (t > end) break;
      const d = jstDayOf(t);
      if (d !== day) {
        day = d;
        got = 0;
      }
      if (got >= cap) {
        // その日の分は回復しきった：翌0時から数え直す
        const m = nextJstMidnight(t);
        if (m > end) break;
        at = m;
        continue;
      }
      if (value < max) value++;
      else reserve++;
      got++;
      at = t;
    }
    if (end === now) {
      const full = value >= max && reserve >= rmax;
      if (!full) fastNext = got >= cap && jstDayOf(at + fp) === day ? nextJstMidnight(at + fp) + fp : at + fp;
      return { value, reserve, max, reserveMax: rmax, nextAt: fastNext, state: { value, reserve, at: full ? now : at, ...(day ? { day, got } : {}) } };
    }
  }
  let ticks = Math.floor((now - at) / p);
  // value を max まで、残りを予備へ
  const toValue = Math.max(0, Math.min(ticks, max - value));
  value += toValue;
  ticks -= toValue;
  const toReserve = Math.max(0, Math.min(ticks, rmax - reserve));
  reserve += toReserve;
  ticks -= toReserve;
  const full = value >= max && reserve >= rmax;
  at = full ? now : at + (toValue + toReserve) * p;
  return { value, reserve, max, reserveMax: rmax, nextAt: full ? null : at + p, state: { value, reserve, at, ...(day ? { day, got } : {}) } };
}

function settle(profile: Profile, now: number) {
  profile.stamina = staminaNow(profile.stamina, now, profile).state;
}

/** 合計（本体＋予備）が足りれば消費して true。本体から先に使う */
export function spendStamina(profile: Profile, now: number, amount: number): boolean {
  settle(profile, now);
  const s = profile.stamina;
  if (s.value + s.reserve < amount) return false;
  const fromValue = Math.min(s.value, amount);
  s.value -= fromValue;
  s.reserve -= amount - fromValue;
  return true;
}

/** 返却・配布（ログボ・寄進・返却）。本体に入り、最大値を越えてもよい */
export function addStamina(profile: Profile, now: number, amount: number) {
  settle(profile, now);
  profile.stamina.value += amount;
}
export const refundStamina = addStamina;

/** 兵糧を最大値まで回復（今は主Lvアップでは使わない。運営の補填などの受け口） */
export function fullRecover(profile: Profile, now: number) {
  settle(profile, now);
  profile.stamina.value = Math.max(profile.stamina.value, staminaMax(profile));
}

/** 戦場の報酬の重み（以前の消費兵糧：章1で5 → 章10で10、上の難易度は+1/段）。EXP の計算に使う */
export function stageRewardWeight(stageId: number, difficulty = 0): number {
  const w = CONFIG.rewards.stageWeight;
  return Math.round(w.min + ((w.max - w.min) * (chapterOf(stageId) - 1)) / 9) + difficulty * w.perDifficulty;
}

/** ステージの消費兵糧（GAME_DESIGN 20章で1戦1。設定 stamina.costMin/costMax/costPerDifficulty） */
export function stageStaminaCost(stageId: number, difficulty = 0): number {
  const c = CONFIG.stamina;
  const ch = chapterOf(stageId);
  return Math.round(c.costMin + ((c.costMax - c.costMin) * (ch - 1)) / 9) + difficulty * c.costPerDifficulty;
}
